import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { AddAppProfileApac } from "src/app/features/ApplicationProfile/components/buttons/AddAppProfileApac";
import { AddAppProfileEft4f } from "src/app/features/ApplicationProfile/components/buttons/AddAppProfileEft4f";
import { ApplicationProfilesSearchForm } from "src/app/features/ApplicationProfile/components/forms/ApplicationProfilesSearchForm";
import { ApplicationProfilesTable } from "src/app/features/ApplicationProfile/components/tables/ApplicationProfilesTable";
import type { ApplicationProfileList } from "src/app/features/ApplicationProfile/types/ApplicationProfileList";
import { useUpdateTableDataMutation } from "../../../../components";
import { PageLayout, PageTitle } from "../../../../components/AppLayout/components/Content/components";
import { NoAccessLayout } from "../../../../components/AppLayout/components/Content/components/NoAccessLayout";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../../components/NotificationAlerts/types";
import { Actions, ResourceManager } from "../../../../components/ResourceManager/ResourceManager";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { canAccess } from "../../../../core/router/auth/permissions";
import { ResourceName, ResourceVariant } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";
import type { NoneOwnProps } from "../../../../core/types";
import type { Entity } from "../../../../core/types/fe";

export const ApplicationProfilesPage: FC<NoneOwnProps> = () => {
    const table = <ApplicationProfilesTable />;
    const searchForm = <ApplicationProfilesSearchForm />;
    const hasAccess = canAccess(ResourceName.APPLICATION_PROFILE_LISTS, "canRead");

    const [copyRequest, { isLoading }] = useUpdateTableDataMutation();
    const baseURI = `${ResourceName.DATA_GROUPS}/${getAssignedDataGroup()?.code}`;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const copyCallback = (record: ApplicationProfileList) => {
        copyRequest({
            url: `${baseURI}/${ResourceName.APPLICATION_PROFILE_LISTS}/${record.id}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            const payloadIdObject = payload as Entity;
            const route = record.apac
                ? `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${payloadIdObject?.id}`
                : `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${FORM_ACTION.EDIT}/${payloadIdObject?.id}`;
            navigate(route);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    };

    return (
        <PageLayout name="appProfiles_page">
            {hasAccess ? (
                <>
                    <PageTitle translationKey="menu.applicationsProfiles" />
                    <ResourceManager<ApplicationProfileList>
                        useDataGroup
                        dataMapperKey="applicationProfilesTableData"
                        resource={ResourceName.APPLICATION_PROFILE_LISTS}
                        table={table}
                        searchForm={searchForm}
                        customActions={[
                            {
                                action: <AddAppProfileApac />,
                                permission: "xml",
                            },
                            {
                                action: <AddAppProfileEft4f />,
                                permission: "eft4f",
                            },
                        ]}
                        actions={[Actions.DELETE, Actions.COPY, Actions.EXPORT]}
                        actionCallbacks={{ copy: copyCallback }}
                        isLoading={isLoading}
                    />
                </>
            ) : (
                <NoAccessLayout />
            )}
        </PageLayout>
    );
};
